/**
 * GIWAHS — Frontend payments client.
 *
 * Thin wrapper around the FastAPI Stripe endpoints. All paid pricing lives
 * server-side (never accept amounts from the frontend), so this module only
 * passes the tier identifier and supplier context.
 */
import { getSupabaseClient } from './supabaseClient';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

async function _authHeaders() {
  const sb = getSupabaseClient();
  const { data: { session } } = await sb.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Sign-in required to start payment.');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export const paymentsClient = {
  /** Public pricing for ladder/profile pages. No auth. */
  async getPricing() {
    const r = await fetch(`${API_BASE}/api/payments/pricing`);
    if (!r.ok) throw new Error(`Pricing fetch failed: ${r.status}`);
    return r.json();
  },

  /** Create a Stripe Checkout Session and return the redirect URL. */
  async createCheckout({ tier, supplier_slug, request_id }) {
    const headers = await _authHeaders();
    const r = await fetch(`${API_BASE}/api/payments/checkout/session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        tier,
        supplier_slug,
        request_id,
        origin_url: window.location.origin,
      }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.detail || `Checkout failed: ${r.status}`);
    return data;
  },

  /** Poll Stripe Checkout status. Backend flips paid=true via RPC on first paid hit. */
  async getStatus(session_id) {
    const headers = await _authHeaders();
    const r = await fetch(`${API_BASE}/api/payments/checkout/status/${encodeURIComponent(session_id)}`, {
      method: 'GET',
      headers,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.detail || `Status fetch failed: ${r.status}`);
    return data;
  },
};
